import { useEffect, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import feedback from "../../../api/asyncThuncks/feedback";
import { useAppDispatch } from "../../../hooks/redux-hooks";
import { TFeedbackType } from "../../../types/feedback";
import {
  requiredMessage,
  validateText,
  validateEmail,
  validateLink,
} from "./feedbackValidation";

type ContactFormValues = {
  name: string;
  email: string;
  subject: string;
  socialNetworks: string;
  fileLinks: { value: string }[];
  label: string;
  message: string;
};

const emptyValues = (): ContactFormValues => ({
  name: "",
  email: "",
  subject: "",
  socialNetworks: "",
  label: "",
  fileLinks: [{ value: "" }],
  message: "",
});

const ContactForms = ({ onSuccess }: { onSuccess: () => void }) => {
  const dispatch = useAppDispatch();
  const [type, setType] = useState<TFeedbackType>("other");
  const [submitError, setSubmitError] = useState("");
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormValues>({ defaultValues: emptyValues() });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "fileLinks",
  });

  const selectType = () => {
    setType(type === "artist" ? "other" : "artist");
    setSubmitError("");
    reset(emptyValues());
  };

  const onSubmit = handleSubmit(async (data) => {
    setSubmitError("");
    try {
      await dispatch(
        feedback({
          type,
          name: data.name.trim(),
          email: data.email.trim(),
          subject: data.subject.trim(),
          message: data.message.trim(),
          ...(type === "artist"
            ? {
                socialNetworks: data.socialNetworks.trim(),
                label: data.label.trim(),
                fileLinks: data.fileLinks.map(({ value }) => value.trim()),
              }
            : {}),
        })
      ).unwrap();
      if (mounted.current) onSuccess();
    } catch {
      if (mounted.current) {
        setSubmitError("Не вдалося надіслати форму. Спробуйте ще раз.");
      }
    }
  });

  const inputClassName = (invalid: boolean) =>
    `box-border w-full rounded-md border-[1.5px] px-3 py-2 text-base outline-none ${
      invalid
        ? "border-[#EC221F] focus:border-[#EC221F]"
        : "border-light_gray focus:border-2 focus:border-orange"
    }`;
  const error = (id: string, message?: string) => (
    <p id={id} className="mt-1 min-h-[1.25rem] text-[13px] text-[#EC221F]">
      {message || "\u00a0"}
    </p>
  );

  return (
    <div className="w-full sm:w-[560px]">
      <h3 id="modal-title" className="pb-4 text-center text-xl font-bold">
        {type === "artist" ? "Артистам" : "Загальні питання"}
      </h3>
      <form onSubmit={onSubmit} noValidate>
        <label className="block text-[13px]">
          Ім'я
          <input
            placeholder={
              type === "artist"
                ? "Введіть назву гурту або сценічне ім'я"
                : "Введіть ваше ім'я"
            }
            className={inputClassName(!!errors.name)}
            aria-invalid={!!errors.name}
            aria-describedby="name-error"
            {...register("name", {
              validate: (value) => validateText(value, 2, 100),
            })}
          />
        </label>
        {error("name-error", errors.name?.message)}
        <label className="block text-[13px]">
          Email
          <input
            type="email"
            placeholder="Введіть адресу вашої пошти"
            className={inputClassName(!!errors.email)}
            aria-invalid={!!errors.email}
            aria-describedby="email-error"
            {...register("email", { validate: validateEmail })}
          />
        </label>
        {error("email-error", errors.email?.message)}
        <label className="block text-[13px]">
          Тема
          <input
            placeholder="Вкажіть тему звернення"
            className={inputClassName(!!errors.subject)}
            aria-invalid={!!errors.subject}
            aria-describedby="subject-error"
            {...register("subject", {
              validate: (value) => validateText(value, 2),
            })}
          />
        </label>
        {error("subject-error", errors.subject?.message)}
        {type === "artist" && (
          <>
            <label className="block text-[13px]">
              Instagram
              <input
                placeholder="Додайте посилання"
                className={inputClassName(!!errors.socialNetworks)}
                aria-invalid={!!errors.socialNetworks}
                aria-describedby="social-error"
                {...register("socialNetworks", {
                  validate: (value) => (value.trim() ? true : requiredMessage),
                })}
              />
            </label>
            {error("social-error", errors.socialNetworks?.message)}
            <fieldset>
              <legend className="text-[13px]">Посилання・3 шт</legend>
              {fields.map((field, index) => (
                <div key={field.id}>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      aria-label={`Посилання ${index + 1}`}
                      placeholder="Надайте посилання на преспак, пісню та ID (Google Диск, YouTube чи Spotify)"
                      className={inputClassName(
                        !!errors.fileLinks?.[index]?.value
                      )}
                      aria-invalid={!!errors.fileLinks?.[index]?.value}
                      aria-describedby={`link-${index}-error`}
                      {...register(`fileLinks.${index}.value`, {
                        validate: validateLink,
                      })}
                    />
                    {index === 0 ? (
                      <button
                        type="button"
                        aria-label="Додати посилання"
                        disabled={fields.length >= 3 || isSubmitting}
                        onClick={() => {
                          if (fields.length < 3) append({ value: "" });
                        }}
                        className="shrink-0 rounded-md px-3 text-sm disabled:opacity-40"
                      >
                        🔗+
                      </button>
                    ) : (
                      <button
                        type="button"
                        aria-label={`Видалити посилання ${index + 1}`}
                        disabled={isSubmitting}
                        onClick={() => remove(index)}
                        className="shrink-0 rounded-md px-3 text-sm"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  {error(
                    `link-${index}-error`,
                    errors.fileLinks?.[index]?.value?.message
                  )}
                </div>
              ))}
            </fieldset>
            <label className="block text-[13px]">
              Авторські права
              <input
                placeholder="Пісня передана в управління ОКУ? На якому лейблі видавався трек?"
                className={inputClassName(!!errors.label)}
                aria-invalid={!!errors.label}
                aria-describedby="label-error"
                {...register("label", {
                  validate: (value) => validateText(value, 2, 500),
                })}
              />
            </label>
            {error("label-error", errors.label?.message)}
          </>
        )}
        <label className="block text-[13px]">
          Ваше повідомлення{type === "artist" ? " (необовʼязково)" : ""}
          <textarea
            placeholder="Напишіть нам тут або на radio-silpo@fozzy.ua"
            className={`${inputClassName(!!errors.message)} min-h-[7rem]`}
            aria-invalid={!!errors.message}
            aria-describedby="message-error"
            {...register("message", {
              validate: (value) =>
                validateText(value, 10, 2000, type === "other"),
            })}
          />
        </label>
        {error("message-error", errors.message?.message)}
        {submitError && (
          <p role="alert" className="mb-3 text-sm text-[#EC221F]">
            {submitError}
          </p>
        )}
        <div className="flex flex-wrap justify-center gap-3">
          <button
            type="submit"
            disabled={isSubmitting}
            className="rounded-md bg-orange px-4 py-2 text-sm text-white transition-opacity hover:opacity-80 disabled:opacity-50"
          >
            {isSubmitting ? "Надсилаємо..." : "Відправити повідомлення"}
          </button>
          <button
            type="button"
            disabled={isSubmitting}
            onClick={selectType}
            className="rounded-md bg-light_gray px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            {type === "artist" ? "Загальні питання" : "Артистам"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactForms;
